/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { useForm } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import { yupResolver } from '@hookform/resolvers/yup';
import { encodeFormData } from '@repo/utils/encodeFormData';
import {
  GLOBAL_LOCALES,
  useLocalizedNavigation,
} from '@repo/gatsby-localization-helpers';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import { useLocalizedYup } from '../hooks';
import Button from '../button';
import BlockContent from '../sanity/BlockContent';

const useValidationSchema = () => {
  const { tt } = useUiComponentsInternationalContext();
  const yup = useLocalizedYup();

  return useMemo(
    () =>
      yup.object().shape({
        name: yup
          .string()
          .trim()
          .requiredLocalized()
          .maxLocalized(50)
          .minLocalized(1)
          .label(tt(s => s.contactFormComponent.nameLabel)),
        email: yup
          .string()
          .emailLocalized()
          .requiredLocalized()
          .label(tt(s => s.contactFormComponent.emailLabel)),
        phone: yup
          .string()
          .requiredLocalized()
          .minLocalized(10)
          .default('')
          .label(tt(s => s.contactFormComponent.phoneNumberLabel)),
        question: yup
          .string()
          .trim()
          .requiredLocalized()
          .label(tt(s => s.contactFormComponent.questionLabel)),
      }),
    [tt, yup]
  );
};

function ContactForm({ value, getInternalLink }) {
  const { tt, locale } = useUiComponentsInternationalContext();
  const validationSchema = useValidationSchema();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });
  const theme = useTheme();
  const [recaptchaVerification, setRecaptchaVerification] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitButtonDisabled, disableSubmitButton] = useState(false);
  const {
    consentMessage,
    consentRequest,
    contactReason,
    successPage,
    errorPage,
  } = value;

  const navigate = useLocalizedNavigation();

  const successPageUrl = successPage?.slug?.current
    ? getInternalLink({ reference: successPage })
    : '/contact-us/success';
  const errorPageUrl = errorPage?.slug?.current
    ? getInternalLink({ reference: errorPage })
    : '/contact-us/fail';

  const onSubmit = (data, e) => {
    e.preventDefault();

    const formName =
      locale === GLOBAL_LOCALES.English.id
        ? 'Contact Us'
        : `Contact Us - ${locale}`;

    if (recaptchaVerification) {
      disableSubmitButton(true);
      const { name, email, phone, product, question, consent } = data;
      const body = encodeFormData({
        'form-name': formName,
        name,
        email,
        phone,
        product,
        question,
        consent,
        'g-recaptcha-response': recaptchaVerification,
      });

      axios({
        method: 'POST',
        url: '/',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: body,
      })
        .then(() => {
          navigate(successPageUrl);
        })
        .catch(() => {
          navigate(errorPageUrl);
        });
    }
  };

  const submitButtonClicked = () => {
    setFormSubmitted(true);
  };

  const recaptchaVerifyCallback = response => {
    setRecaptchaVerification(response);
  };

  const formName =
    locale === GLOBAL_LOCALES.English.id
      ? 'Contact Us'
      : `Contact Us - ${locale}`;

  return (
    <>
      <p>{tt(s => s.contactFormComponent.allFieldsRequired)}</p>
      <theme.forms.contact.styledForm
        onSubmit={handleSubmit(onSubmit)}
        name={formName}
        method="POST"
        action={successPageUrl}
        data-netlify="true"
        data-netlify-recaptcha="true"
      >
        <input type="hidden" name="form-name" value="Contact Us" />
        <theme.forms.contact.formRow width="55" invalid={errors.name}>
          <label htmlFor="name">
            {tt(s => s.contactFormComponent.nameLabel)}
            <input type="text" aria-required="true" {...register('name')} />
          </label>
          <theme.forms.contact.validationMessage>
            {errors.name && errors.name.message}
          </theme.forms.contact.validationMessage>
        </theme.forms.contact.formRow>
        <theme.forms.contact.formRow width="55" invalid={errors.email}>
          <label htmlFor="email">
            {tt(s => s.contactFormComponent.emailLabel)}
            <input maxLength="50" aria-required="true" {...register('email')} />
          </label>
          <theme.forms.contact.validationMessage>
            {errors.email && errors.email.message}
          </theme.forms.contact.validationMessage>
        </theme.forms.contact.formRow>
        <theme.forms.contact.formRow width="55" invalid={errors.phone}>
          <label htmlFor="phone">
            {tt(s => s.contactFormComponent.phoneNumberLabel)}
            <input
              placeholder="(___) ___-____"
              maxLength="14"
              aria-required="true"
              {...register('phone')}
            />
          </label>
          <theme.forms.contact.validationMessage>
            {errors.phone && errors.phone.message}
          </theme.forms.contact.validationMessage>
        </theme.forms.contact.formRow>
        <theme.forms.contact.formRow width="55">
          <label htmlFor="product">
            {tt(s => s.contactFormComponent.productQuestionLabel)}
            <select {...register('product')} defaultValue="">
              <option value="" disabled="disabled">
                {tt(
                  s => s.contactFormComponent.pleaseChooseDefaultSelectOption
                )}
              </option>
              {contactReason?.length > 0 &&
                contactReason?.map(reason => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
            </select>
          </label>
        </theme.forms.contact.formRow>
        <theme.forms.contact.formRow width="85" invalid={errors.question}>
          <label htmlFor="question">
            {tt(s => s.contactFormComponent.questionOrCommentLabel)}
            <textarea aria-required="true" {...register('question')} />
          </label>
          <theme.forms.contact.validationMessage>
            {errors.question && errors.question.message}
          </theme.forms.contact.validationMessage>
        </theme.forms.contact.formRow>
        <theme.forms.contact.formRow width="85">
          <theme.forms.contact.formCheck>
            <label htmlFor="consent">
              <input
                {...register('consent')}
                type="checkbox"
                id="consent"
                value="true"
              />
              {consentMessage}
            </label>
          </theme.forms.contact.formCheck>
        </theme.forms.contact.formRow>
        <theme.forms.contact.formRow
          width="85"
          invalid={formSubmitted && !recaptchaVerification}
        >
          <BlockContent
            blocks={consentRequest}
            getInternalLink={getInternalLink}
          />
          <ReCAPTCHA
            hl={locale}
            sitekey={process.env.GATSBY_SPORTDOG_RECAPTCHA_KEY}
            onChange={recaptchaVerifyCallback}
          />
          {formSubmitted && !recaptchaVerification && (
            <theme.forms.contact.validationMessage>
              {tt(s => s.contactFormComponent.proveYouAreNotRobot)}
            </theme.forms.contact.validationMessage>
          )}
        </theme.forms.contact.formRow>
        <theme.forms.contact.formRow>
          <Button
            type="submit"
            onClick={submitButtonClicked}
            disabled={submitButtonDisabled}
          >
            {tt(s => s.contactFormComponent.submitButton)}
          </Button>
        </theme.forms.contact.formRow>
      </theme.forms.contact.styledForm>
    </>
  );
}

ContactForm.propTypes = {
  value: PropTypes.shape({
    consentMessage: PropTypes.string,
    consentRequest: PropTypes.arrayOf(PropTypes.shape({})),
    contactReason: PropTypes.arrayOf(PropTypes.string),
    successPage: PropTypes.shape({
      slug: PropTypes.shape({
        current: PropTypes.string,
      }),
    }),
    errorPage: PropTypes.shape({
      slug: PropTypes.shape({
        current: PropTypes.string,
      }),
    }),
  }).isRequired,
  getInternalLink: PropTypes.func.isRequired,
};

export default ContactForm;
